<script setup lang="ts">
import "vue3-carousel/dist/carousel.css";

import LeButton from "@dsk/components/Button/LeButton.vue";
import LeCard from "@dsk/components/Card/LeCard.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeLabel from "@dsk/components/Label/LeLabel.vue";
import LeModal from "@dsk/components/LeModal.vue";
import LeChevronLeft from "@dsk/icons/LeChevronLeft.vue";
import LeChevronRight from "@dsk/icons/LeChevronRight.vue";
import type { Label, Link } from "@dsk/types";
import { format } from "date-fns";
import { clone } from "lodash-es";
import { computed, onBeforeMount, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Carousel, Slide } from "vue3-carousel";

import AbcPromotionVideoUrl from "@/assets/img/home/cards/abc-promotion.mp4";
import AbcPromotionHighDpiImgUrl from "@/assets/img/home/cards/abc-promotion.png";
import AbcPromotionImgUrl from "@/assets/img/home/cards/abc-promotion.png?halfSize";
import DictsysClick from "@/assets/img/home/cards/dictsys-click.mp4";
import IceCreamHighDpiImgUrl from "@/assets/img/home/cards/ice-cream.png";
import IceCreamImgUrl from "@/assets/img/home/cards/ice-cream.png?halfSize";
import LaptopHighDpiImgUrl from "@/assets/img/home/cards/laptop.png";
import LaptopImgUrl from "@/assets/img/home/cards/laptop.png?halfSize";
import PlusGiftHighDpiImgUrl from "@/assets/img/home/cards/plus-gift.png";
import PlusGiftImgUrl from "@/assets/img/home/cards/plus-gift.png?halfSize";
import WriteAssistantLaptopHighDpiImgUrl from "@/assets/img/home/cards/write-assistant-laptop.png";
import WriteAssistantLaptopImgUrl from "@/assets/img/home/cards/write-assistant-laptop.png?halfSize";
import { useAppLink, useResponsiveness } from "@/composables";
import { config } from "@/config";
import { MatomoHelper } from "@/helpers";
import { useUserStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";
import { gridBreakpoints } from "@/utils";

const { t } = useI18n();
const { chromeExtensionLink } = useAppLink();
const { isMobile } = config.app;

interface Card {
  link?: Link;
  labels?: Label[];
  isFeature: boolean;
  headline: string;
  headerSrc: string;
  headerHighDpiSrc: string;
  headerType: "Image" | "Video";
  publishDate: Date;
  cardBody: string;
}

const carousel = ref();

const summerSupportCard: Card = {
  isFeature: false,
  headline: t("le-news.summer.title"),
  headerSrc: IceCreamImgUrl,
  headerHighDpiSrc: IceCreamHighDpiImgUrl,
  headerType: "Image",
  publishDate: new Date("2025-07-07"),
  cardBody: `<span style="white-space: pre-wrap">${t("le-news.summer.text")}</span>`
};

const customWriteAssistantCard: Card = {
  isFeature: false,
  headline: t("le-news.custom-write-assistant.title"),
  headerSrc: WriteAssistantLaptopImgUrl,
  headerHighDpiSrc: WriteAssistantLaptopHighDpiImgUrl,
  headerType: "Image",
  publishDate: new Date("2025-04-30"),
  cardBody: ""
};

const dictsysClickCard: Card = {
  isFeature: false,
  headline: t("le-news.doubleclick.titel"),
  headerSrc: DictsysClick,
  headerHighDpiSrc: DictsysClick,
  headerType: "Video",
  publishDate: new Date("2025-03-25"),
  cardBody: `
    <p>${t("le-news.doubleclick.text")}</p>
    <video src="${DictsysClick}" playsinline muted autoplay loop disableRemotePlayback disablePictureInPicture />
  `
};

const chromeExtensionCard: Card = {
  isFeature: false,
  headline: t("le-front-page-carousel.chrome-extension.title"),
  headerSrc: LaptopImgUrl,
  headerHighDpiSrc: LaptopHighDpiImgUrl,
  headerType: "Image",
  publishDate: new Date("2023-01-04"),
  cardBody: `
  <p>${t("le-front-page-carousel.chrome-extension.paragraph1")}</p>
  <p>${t("le-front-page-carousel.chrome-extension.paragraph2")}</p>
  <p>${t("le-front-page-carousel.chrome-extension.paragraph3", {
    link: `<a href="${chromeExtensionLink}" target="_blank">${t("le-front-page-carousel.chrome-extension.paragraph3-link-text")}</a>`
  })}</p>`
};

const plusCard: Card = {
  isFeature: false,
  headline: t("plus.card-title"),
  headerSrc: PlusGiftImgUrl,
  headerHighDpiSrc: PlusGiftHighDpiImgUrl,
  headerType: "Image",
  link: {
    linkText: t("plus.button-text"),
    link: "/plus?mtm_campaign=plus-link&mtm_kwd=card"
  },
  labels: [
    {
      labelText: t("plus.only-you-label"),
      labelColor: "neutral"
    }
  ],
  publishDate: new Date("2023-06-14"),
  cardBody: `
        <p>
          ${t("plus.card-text")}
        </p>`
};

const abcPromotionCard: Card = {
  isFeature: false,
  headline: t("le-front-page-carousel.abc-promotion.title"),
  headerSrc: AbcPromotionImgUrl,
  headerHighDpiSrc: AbcPromotionHighDpiImgUrl,
  headerType: "Image",
  labels: [
    {
      labelText: "Til skoler"
    }
  ],
  publishDate: new Date("2024-12-15"),
  cardBody: `
      <div style="margin-top: 10px; margin-left: -15px;">
        ${t("le-front-page-carousel.abc-promotion.paragraph1")}
      </div>
      <p style="margin-top: 15px">${t("le-front-page-carousel.abc-promotion.paragraph2")}</p>

      <video src="${AbcPromotionVideoUrl}" playsinline muted autoplay loop disableRemotePlayback disablePictureInPicture />
    `
};

const cardData = reactive<Card[]>([dictsysClickCard, chromeExtensionCard]);

const userStore = useUserStore();
const userDataLoaded = ref<boolean>(false);

onBeforeMount(async () => {
  const userData = await userStore.getUser();
  if (!isMobile && userData.featureFlags.showPlusLink) {
    cardData.push(plusCard);
  }

  if (userData.featureFlags.showAbcPromotion) {
    cardData.push(abcPromotionCard);
  }

  if (!isMobile && userData.featureFlags.availableWriteAssistantPromotion) {
    const appPromotion = userData.featureFlags.availableWriteAssistantPromotion.type === "app";

    customWriteAssistantCard.cardBody = appPromotion
      ? `<p>${t("le-news.custom-write-assistant.text.app")}</p>`
      : `<p>${t("le-news.custom-write-assistant.text.web")}</p>`;

    customWriteAssistantCard.link = {
      linkText: appPromotion
        ? t("le-news.custom-write-assistant.button.app")
        : t("le-news.custom-write-assistant.button.web"),
      link: userData.featureFlags.availableWriteAssistantPromotion.url,
      event: {
        category: MatomoEventCategory.WriteAssistantPromotion,
        action: MatomoEventAction.ClickNewsCardLink,
        label: `Clicked on "${userData.featureFlags.availableWriteAssistantPromotion.type}" news card link`
      }
    };

    cardData.push(customWriteAssistantCard);
  }

  if ([28, 29, 30].includes(parseInt(format(new Date(), "w")))) {
    cardData.push(summerSupportCard);
  }

  userDataLoaded.value = true;
});

const cards = computed<Card[]>(() =>
  clone(cardData).sort((a: Card, b: Card) => b.publishDate.getTime() - a.publishDate.getTime())
);

const formatPublishDate = (date: Date): string => `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
const { isSmallerThanDesktopScreen, isSmallerThanTabletScreen } = useResponsiveness();

const isCarouselDraggingEnabled = computed<boolean>(
  () =>
    (isSmallerThanTabletScreen.value && cards.value.length > 1) ||
    (!isSmallerThanTabletScreen.value && cards.value.length > 3)
);

const carouselSettings = { itemsToShow: 1, snapAlign: "center" };
const carouselBreakpoints = {
  [gridBreakpoints.sm]: { itemsToShow: 2, snapAlign: "start" },
  [gridBreakpoints.md]: { itemsToShow: 3, snapAlign: "start" }
};

const trackOpenedNews = (headline: string) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.News,
    MatomoEventAction.OpenNewsCard,
    'Opened news card "' + headline + '"'
  );
};

const trackLink = (link: Link | undefined) => {
  if (link && link.event) {
    MatomoHelper.sendEvent(link.event.category, link.event.action, link.event.label);
  }
};

// Fixes mobile card width calculation in vue3-carousel.
// iOS Safari has issues triggering random resize events,
// could possibly be fixed with: https://boagworld.com/dev/ios-safari-resizing-issues/
// but this breaks the footer currently.
// remember :transition if prefers-reduced-motion ever becomes an issue
const carouselMargin = ref(isSmallerThanDesktopScreen.value ? "0px" : "-15px");
onMounted(() => setTimeout(() => (carouselMargin.value = "-15px"), 50));
</script>

<template>
  <div class="container le-front-page-carousel">
    <div class="row">
      <div class="le-front-page-carousel__heading col-xs-12">
        <LeHeading tag="h3" size="l">{{ t("le-front-page-carousel.exciting-news") }}</LeHeading>
      </div>
      <div class="col-xs-12 mb-5">
        <Carousel
          v-if="userDataLoaded"
          ref="carousel"
          :transition="75"
          :mouse-drag="isCarouselDraggingEnabled"
          :touch-drag="isCarouselDraggingEnabled"
          :settings="carouselSettings"
          :breakpoints="carouselBreakpoints"
        >
          <Slide v-for="(currentCard, i) in cards" :key="i">
            <div class="le-front-page-carousel__slide">
              <LeCard
                :link="currentCard.link"
                :labels="currentCard.labels"
                :is-feature="currentCard.isFeature"
                :headline="currentCard.headline"
                :header-src="currentCard.headerSrc"
                :header-high-dpi-src="currentCard.headerHighDpiSrc"
                :header-type="currentCard.headerType"
                :publish-date="currentCard.publishDate"
                @open="trackOpenedNews(currentCard.headline)"
                @link="trackLink(currentCard.link)"
              >
                <div inert v-html="currentCard.cardBody"></div>
                <template v-if="!currentCard.link" #modal="{ isModalOpen, closeModal }">
                  <LeModal color="primary" :is-open="isModalOpen" @close="closeModal()">
                    <template #title>
                      <LeHeading tag="h2" size="m">{{ currentCard.headline }}</LeHeading>
                    </template>
                    <template #body>
                      <LeLabel class="le-front-page-carousel__modal-label">
                        {{ formatPublishDate(currentCard.publishDate) }}
                      </LeLabel>
                      <div class="le-front-page-carousel__modal-body" v-html="currentCard.cardBody"></div>
                    </template>
                    <template #buttons>
                      <LeButton color="neutral" size="small" @click="closeModal()">{{
                        t("le-front-page-carousel.close-modal-label")
                      }}</LeButton>
                    </template>
                  </LeModal>
                </template>
              </LeCard>
            </div>
          </Slide>
        </Carousel>
        <div v-if="isCarouselDraggingEnabled" class="le-front-page-carousel__swipe">
          {{ t("le-front-page-carousel.swipe") }}
        </div>
        <div
          v-if="isCarouselDraggingEnabled && carousel?.data !== undefined"
          class="le-front-page-carousel__button-wrapper"
        >
          <button
            class="le-front-page-carousel__button"
            :class="{ 'le-front-page-carousel__button--disabled': carousel.data.currentSlide == 0 }"
            :aria-label="t('le-front-page-carousel.previous-button')"
            @click="carousel.prev()"
          >
            <LeChevronLeft role="presentation" />
          </button>
          <button
            class="le-front-page-carousel__button"
            :class="{
              'le-front-page-carousel__button--disabled': carousel.data.currentSlide >= carousel.data.maxSlide
            }"
            :aria-label="t('le-front-page-carousel.next-button')"
            @click="carousel.next()"
          >
            <LeChevronRight role="presentation" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

.le-front-page-carousel {
  &__heading {
    margin-bottom: -12px;
  }

  &__modal-label {
    width: fit-content;
    margin-bottom: 14px;
  }

  &__modal-body {
    :deep(a) {
      border-radius: 4px;
      color: @colors[@neutrals][400];
      text-decoration: underline;

      &:hover {
        color: @colors[@neutrals][400];
      }

      &:focus-within {
        .focus-outline(green);
      }
    }
  }

  &__slide {
    max-width: 100%;
    padding-right: 4px;
    padding-left: 4px;

    #media-breakpoint-up(lg, {
      padding-left: 15px;
      padding-right: 15px;
    });
  }

  &__swipe {
    margin-top: -8px;
    margin-bottom: 0;
    color: @colors[@neutrals][500];
    font-family: @font-family-body;
    font-style: italic;
    text-align: center;

    @media (pointer: fine) {
      display: none;
    }
  }

  &__button {
    display: flex;
    justify-self: center;
    padding: 8px;
    border: none;
    border-radius: 50%;
    background: none;
    color: @colors[@secondary][800];
    cursor: pointer;

    &:focus-within {
      .focus-outline(green);
    }

    &--disabled {
      opacity: 0.3;
      cursor: unset;
    }
  }

  &__button-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-content: center;
    width: 33%;
    margin: 0 auto;

    @media not (pointer: fine) {
      display: none;
    }
  }
}

:deep(.carousel) {
  --vc-icn-width: 24px;

  margin-right: v-bind("carouselMargin");
  margin-left: v-bind("carouselMargin");
  text-align: left;
}

:deep(.carousel__next),
:deep(.carousel__prev) {
  display: none;
  color: @colors[@secondary][800];

  #media-breakpoint-up(lg, {
    display: flex;
  });

  &:focus {
    outline: auto !important;
  }

  &--disabled {
    opacity: 0.3;
  }
}

:deep(.carousel__prev) {
  left: -32px;

  #media-breakpoint-up(xl, {
    left: -48px;
  });
}

:deep(.carousel__next) {
  right: -32px;

  #media-breakpoint-up(xl, {
    right: -48px;
  });
}

:deep(.carousel__slide) {
  padding: 25px 0; // Make sure card shadow isn t cutoff.
}

:deep(.carousel__viewport) {
  padding-right: 15px;
  padding-left: 15px;

  #media-breakpoint-up(lg, {
    padding-right: 0;
    padding-left: 0;
  });
}

:deep(p) {
  margin-bottom: 24px;
  .body-text("m");
}

:deep(a) {
  text-decoration: underline;
}
</style>
